import { ENTRYPOINT } from '../../config/entrypoint';

export const courseNumber = (id) => String(id).split('/').pop();
export const revisionApi = async (path, body) => {
  const response = await fetch(`${ENTRYPOINT}/seo${path}`, {
    method: body ? 'POST' : 'GET',
    headers: {
      Authorization: `Bearer ${localStorage.getItem('auth')}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  let data;
  try {
    data = await response.json();
  } catch {
    const error = new Error(
      response.status === 409
        ? 'The course or its versions changed. Your edits are still in the form. Copy them before reopening the course.'
        : `The server returned an unexpected response (${response.status}). Your edits are still in the form.`
    );
    error.status = response.status;
    throw error;
  }
  if (!response.ok) {
    const error = new Error(
      data.detail ||
        data['hydra:description'] ||
        data.message ||
        'The request could not be completed.'
    );
    error.status = response.status;
    throw error;
  }
  return data;
};

export const revisionRecord = (data) => {
  const latest = data.revisions[0];
  const content =
    latest && ['draft', 'review'].includes(latest.status)
      ? latest.content
      : data.current;
  const translationsFormView = {};
  Object.entries(content).forEach(([locale, fields]) => {
    Object.entries(fields).forEach(([field, value]) => {
      (translationsFormView[field] ||= {})[locale] = value;
    });
  });
  return {
    id: `/courses/${data.id}`,
    code: data.code,
    company: data.company,
    companyName: data.companyName,
    enabled: data.enabled,
    title: content.de?.title || Object.values(content)[0]?.title,
    translationsFormView,
    // Keep locale-first snapshots opaque to TranslatableInputs record traversal.
    seoRevision: JSON.stringify(data),
  };
};

export const saveCourseRevision = async (id, data, previousData) => {
  const source = typeof previousData.seoRevision === 'string'
    ? JSON.parse(previousData.seoRevision)
    : previousData.seoRevision;
  // Also support forms already open before snapshots became opaque.
  const snapshotFields = source.fields || Object.keys(Object.values(source.current)[0]);
  const snapshot = (value) => Object.fromEntries(
    Object.entries(value).map(([locale, fields]) => [locale,
      Object.fromEntries(snapshotFields.map(field => [field, fields[field]]))])
  );
  const latest = source.revisions[0];
  const content = {};
  Object.entries(source.current).forEach(([locale, fields]) => {
    content[locale] = {};
    snapshotFields.forEach((field) => {
      content[locale][field] =
        data.translationsFormView[field]?.[locale] ?? null;
    });
  });
  return revisionRecord(
    await revisionApi(`/courses/${courseNumber(id)}/revisions`, {
      action: 'review',
      content,
      baseline:
        latest && ['draft', 'review'].includes(latest.status)
          ? snapshot(latest.baseline)
          : snapshot(source.current),
      latestId: latest?.id ?? null,
    })
  );
};

export const reviewStatusLabel = (status) =>
  ({
    review: 'Needs review',
    draft: 'Draft',
    approved: 'Approved',
  })[status] || '';

// Keep normal resource URLs while the restricted role uses isolated draft operations.
export const withCourseRevisions = (provider, getUser) => ({
  ...provider,
  async getList(resource, params) {
    const roles = getUser()?.roles || [];
    if (resource === 'courses' && roles.includes('ROLE_SEO_EDITOR')) {
      const query = new URLSearchParams({
        page: params.pagination?.page || 1,
        perPage: params.pagination?.perPage || 10,
        code: params.filter?.code || '',
        title: params.filter?.['translations.title'] || '',
        seoTitle: params.filter?.['translations.seoTitle'] || '',
        seoDescription: params.filter?.['translations.seoDescription'] || '',
        company: params.filter?.company || '',
        reviewOnly: params.filter?.seoNeedsReview ? '1' : '0',
        sort: params.sort?.field || 'code',
        order: params.sort?.order || 'ASC',
      });
      const result = await revisionApi(`/courses?${query}`);
      return {
        data: result.courses.map((c) => ({
          ...c,
          id: `/courses/${c.id}`,
          companyName: c.company,
        })),
        total: result.total,
      };
    }
    const result = await provider.getList(resource, params);
    if (
      resource === 'courses' &&
      roles.some((r) => ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN'].includes(r)) &&
      result.data.length
    ) {
      const ids = [...new Set(result.data.map((c) => courseNumber(c.id)))];
      const statuses = {};
      for (let offset = 0; offset < ids.length; offset += 500) {
        Object.assign(statuses, await revisionApi(`/courses/status?ids=${encodeURIComponent(ids.slice(offset, offset + 500).join(','))}`));
      }
      return {
        ...result,
        data: result.data.map((c) => ({
          ...c,
          reviewStatus: statuses[courseNumber(c.id)] || null,
        })),
      };
    }
    return result;
  },
  async getOne(resource, params) {
    if (
      resource === 'courses' &&
      getUser()?.roles?.includes('ROLE_SEO_EDITOR')
    ) {
      return {
        data: revisionRecord(
          await revisionApi(`/courses/${courseNumber(params.id)}`)
        ),
      };
    }
    return provider.getOne(resource, params);
  },
  async update(resource, params) {
    if (
      resource === 'courses' &&
      getUser()?.roles?.includes('ROLE_SEO_EDITOR')
    ) {
      return {
        data: await saveCourseRevision(
          params.id,
          params.data,
          params.previousData
        ),
      };
    }
    return provider.update(resource, params);
  },
});
